/* eslint-disable no-console, no-restricted-globals, class-methods-use-this */
/**
 * Event Collector - Framework-agnostic event capture with rolling buffer
 * Captures network requests, navigation, errors, and console output
 */

import axios from '@fs/zion-axios'
import { v4 as uuidv4 } from 'uuid'
import { redactHeaders, redactBody, DEFAULT_SENSITIVE_HEADERS, DEFAULT_SENSITIVE_FIELDS } from './redactor'

const DEFAULT_CONFIG = {
  maxEvents: 50,
  maxBodyLength: 10000,
  captureBody: true,
  sensitiveHeaders: DEFAULT_SENSITIVE_HEADERS,
  sensitiveFields: DEFAULT_SENSITIVE_FIELDS,
  persistToStorage: true, // Enable localStorage persistence (shared across tabs)
  storageKey: 'event-capture-buffer',
}

/**
 * EventCollector manages a rolling buffer of captured events
 */
export class EventCollector {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.events = []
    this.subscribers = new Set()
    this.reportModalSubscribers = new Set()
    this.isReportModalOpen = false
    this.reportModalTriggerMessage = null
    this.installed = false

    // Load events from localStorage if persistence is enabled
    if (this.config.persistToStorage) {
      this.loadFromStorage()
    }

    // Store original functions for restoration
    this.originalFetch = null
    this.originalXhrOpen = null
    this.originalXhrSend = null
    this.originalXhrSetRequestHeader = null
    this.originalPushState = null
    this.originalReplaceState = null
    this.originalConsoleLog = null
    this.originalConsoleWarn = null
    this.originalConsoleError = null
    this.originalConsoleInfo = null
    this.originalConsoleDebug = null

    // Store interceptor IDs for cleanup
    this.axiosRequestInterceptorId = null
    this.axiosResponseInterceptorId = null

    // Event listeners to clean up
    this.errorListener = null
    this.unhandledRejectionListener = null
    this.popStateListener = null
    this.hashChangeListener = null
    this.storageListener = null

    // Set up cross-tab synchronization if persistence is enabled
    if (this.config.persistToStorage) {
      this.setupStorageSync()
    }
  }

  /**
   * Load events from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.config.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          this.events = parsed.slice(-this.config.maxEvents)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load events from localStorage:', error)
    }
  }

  /**
   * Save events to localStorage
   */
  saveToStorage() {
    if (!this.config.persistToStorage) {
      return
    }

    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(this.events))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to save events to localStorage:', error)
    }
  }

  /**
   * Clear events from memory and storage
   */
  clearEvents() {
    this.events = []
    if (this.config.persistToStorage) {
      try {
        localStorage.removeItem(this.config.storageKey)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to clear events from localStorage:', error)
      }
    }
    this.notifySubscribers()
  }

  /**
   * Set up cross-tab synchronization via storage events
   */
  setupStorageSync() {
    this.storageListener = (event) => {
      // Only respond to changes to our storage key
      if (event.key === this.config.storageKey && event.storageArea === localStorage) {
        // Reload events from localStorage
        this.loadFromStorage()
        // Notify subscribers that events changed
        this.notifySubscribers()
      }
    }

    window.addEventListener('storage', this.storageListener)
  }

  /**
   * Clean up storage sync listener
   */
  cleanupStorageSync() {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener)
      this.storageListener = null
    }
  }

  /**
   * Add an event to the rolling buffer
   */
  addEvent(event) {
    const eventWithTimestamp = {
      ...event,
      id: uuidv4(),
      timestamp: new Date().toISOString(),
    }

    this.events.push(eventWithTimestamp)

    // Maintain max size by removing oldest
    if (this.events.length > this.config.maxEvents) {
      this.events.shift()
    }

    // Save to storage
    this.saveToStorage()

    // Notify subscribers with a new array reference
    this.notifySubscribers()
  }

  /**
   * Get a snapshot of current events (new array reference each time)
   */
  getSnapshot() {
    return [...this.events]
  }

  /**
   * Subscribe to event buffer changes
   */
  subscribe(callback) {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  /**
   * Notify all subscribers of changes
   */
  notifySubscribers() {
    this.subscribers.forEach((callback) => callback())
  }

  /**
   * Subscribe to report modal state changes
   */
  subscribeToReportModal(callback) {
    this.reportModalSubscribers.add(callback)
    return () => {
      this.reportModalSubscribers.delete(callback)
    }
  }

  /**
   * Get report modal open state
   */
  getReportModalState() {
    return { isOpen: this.isReportModalOpen, triggerMessage: this.reportModalTriggerMessage }
  }

  /**
   * Open the report modal
   */
  openReportModal(triggerMessage = null) {
    this.isReportModalOpen = true
    this.reportModalTriggerMessage = triggerMessage
    this.reportModalSubscribers.forEach((callback) => callback())
  }

  /**
   * Close the report modal
   */
  closeReportModal() {
    this.isReportModalOpen = false
    this.reportModalTriggerMessage = null
    this.reportModalSubscribers.forEach((callback) => callback())
  }

  /**
   * Record a custom event
   */
  recordCustomEvent(eventType, data) {
    this.addEvent({
      type: 'custom',
      eventType,
      data,
    })
  }

  /**
   * Install all interceptors and listeners (idempotent)
   */
  install() {
    if (this.installed) {
      return
    }

    this.installAxiosInterceptors()
    this.installFetchInterceptor()
    this.installXhrInterceptor()
    this.installNavigationInterceptors()
    this.installConsoleInterceptors()
    this.installErrorHandlers()

    this.installed = true
  }

  /**
   * Uninstall all interceptors and restore original APIs
   * Note: Storage sync listener stays active to receive cross-tab updates
   */
  uninstall() {
    if (!this.installed) {
      return
    }

    this.uninstallAxiosInterceptors()
    this.uninstallFetchInterceptor()
    this.uninstallXhrInterceptor()
    this.uninstallNavigationInterceptors()
    this.uninstallConsoleInterceptors()
    this.uninstallErrorHandlers()
    // Note: We do NOT clean up storage sync here - it should stay active
    // to receive updates from other tabs even when this tab isn't capturing

    this.installed = false
  }

  /**
   * Install axios interceptors (works alongside existing spinner interceptors)
   */
  installAxiosInterceptors() {
    const startTimes = new WeakMap()

    // Request interceptor
    this.axiosRequestInterceptorId = axios.interceptors.request.use(
      (config) => {
        startTimes.set(config, Date.now())
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.axiosResponseInterceptorId = axios.interceptors.response.use(
      (response) => {
        const startTime = startTimes.get(response.config)
        const duration = startTime ? Date.now() - startTime : undefined

        this.addEvent({
          type: 'network',
          subtype: 'axios',
          method: response.config.method?.toUpperCase(),
          url: this.buildFullUrl(response.config),
          status: response.status,
          statusText: response.statusText,
          duration,
          requestHeaders: redactHeaders(response.config.headers, this.config.sensitiveHeaders),
          requestBody: redactBody(response.config.data, this.getContentType(response.config.headers), {
            sensitiveFields: this.config.sensitiveFields,
            maxLength: this.config.maxBodyLength,
            captureBody: this.config.captureBody,
          }),
          responseHeaders: redactHeaders(response.headers, this.config.sensitiveHeaders),
          responseBody: redactBody(response.data, response.headers?.['content-type'], {
            sensitiveFields: this.config.sensitiveFields,
            maxLength: this.config.maxBodyLength,
            captureBody: this.config.captureBody,
          }),
        })

        return response
      },
      (error) => {
        const startTime = error.config ? startTimes.get(error.config) : undefined
        const duration = startTime ? Date.now() - startTime : undefined

        this.addEvent({
          type: 'network',
          subtype: 'axios',
          method: error.config?.method?.toUpperCase(),
          url: error.config ? this.buildFullUrl(error.config) : 'unknown',
          status: error.response?.status,
          statusText: error.response?.statusText || error.message,
          duration,
          error: error.message,
          requestHeaders: error.config ? redactHeaders(error.config.headers, this.config.sensitiveHeaders) : undefined,
          requestBody: error.config
            ? redactBody(error.config.data, this.getContentType(error.config.headers), {
                sensitiveFields: this.config.sensitiveFields,
                maxLength: this.config.maxBodyLength,
                captureBody: this.config.captureBody,
              })
            : undefined,
          responseHeaders: error.response
            ? redactHeaders(error.response.headers, this.config.sensitiveHeaders)
            : undefined,
          responseBody: error.response
            ? redactBody(error.response.data, error.response.headers?.['content-type'], {
                sensitiveFields: this.config.sensitiveFields,
                maxLength: this.config.maxBodyLength,
                captureBody: this.config.captureBody,
              })
            : undefined,
        })

        return Promise.reject(error)
      }
    )
  }

  /**
   * Build full URL from axios config
   */
  buildFullUrl(config) {
    const baseURL = config.baseURL || ''
    const url = config.url || ''

    // If URL is absolute, return it
    if (url.match(/^https?:\/\//)) {
      return url
    }

    // Combine baseURL and url
    if (baseURL) {
      return `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`
    }

    return url
  }

  /**
   * Normalize HeadersInit (Headers instance, array of pairs, or plain object) to a plain object
   */
  normalizeHeaders(headers) {
    if (!headers) return headers
    if (typeof headers.entries === 'function') {
      return Object.fromEntries(headers.entries())
    }
    if (Array.isArray(headers)) {
      return Object.fromEntries(headers)
    }
    return headers
  }

  /**
   * Get Content-Type from headers, handling AxiosHeaders and plain objects with mixed casing
   */
  getContentType(headers) {
    if (!headers) return undefined
    if (typeof headers.get === 'function') {
      return headers.get('Content-Type')
    }
    return headers['Content-Type'] ?? headers['content-type']
  }

  /**
   * Uninstall axios interceptors
   */
  uninstallAxiosInterceptors() {
    if (this.axiosRequestInterceptorId !== null) {
      axios.interceptors.request.eject(this.axiosRequestInterceptorId)
      this.axiosRequestInterceptorId = null
    }
    if (this.axiosResponseInterceptorId !== null) {
      axios.interceptors.response.eject(this.axiosResponseInterceptorId)
      this.axiosResponseInterceptorId = null
    }
  }

  /**
   * Install fetch interceptor
   */
  installFetchInterceptor() {
    this.originalFetch = window.fetch.bind(window)

    window.fetch = async (...args) => {
      const startTime = Date.now()
      const [url, options = {}] = args
      const method = options.method || 'GET'
      const normalizedRequestHeaders = this.normalizeHeaders(options.headers)

      try {
        const response = await this.originalFetch(...args)
        const duration = Date.now() - startTime

        // Read body from original response, then reconstruct
        // This avoids keeping TWO copies in memory (original + clone)
        let responseBody = null
        let bodyForCaller = null

        if (this.config.captureBody) {
          const contentType = response.headers.get('content-type')
          const contentLength = response.headers.get('content-length')

          // Skip body capture for very large responses
          if (contentLength && Number.parseInt(contentLength, 10) > 1000000) {
            responseBody = `[Response too large: ${contentLength} bytes - skipped]`
            bodyForCaller = response.body
          } else if (contentType && /^(text|application\/(json|javascript|xml))/.test(contentType)) {
            try {
              // Read the body once; redactBody handles truncation for capture
              const fullText = await response.text()
              responseBody = fullText
              bodyForCaller = fullText
            } catch (error) {
              console.warn('EventCollector: Failed to read response body:', error)
              responseBody = '[Unable to read body]'
              bodyForCaller = null
            }
          } else {
            // Non-text content, don't capture
            responseBody = `[Binary or non-text content: ${contentType || 'unknown'}]`
            bodyForCaller = response.body
          }
        } else {
          bodyForCaller = response.body
        }

        this.addEvent({
          type: 'network',
          subtype: 'fetch',
          method: method.toUpperCase(),
          url: typeof url === 'string' ? url : url.url,
          status: response.status,
          statusText: response.statusText,
          duration,
          requestHeaders: redactHeaders(normalizedRequestHeaders, this.config.sensitiveHeaders),
          requestBody: redactBody(
            options.body,
            normalizedRequestHeaders?.['Content-Type'] ?? normalizedRequestHeaders?.['content-type'],
            {
              sensitiveFields: this.config.sensitiveFields,
              maxLength: this.config.maxBodyLength,
              captureBody: this.config.captureBody,
            }
          ),
          responseHeaders: redactHeaders(Object.fromEntries(response.headers.entries()), this.config.sensitiveHeaders),
          responseBody: redactBody(responseBody, response.headers.get('content-type'), {
            sensitiveFields: this.config.sensitiveFields,
            maxLength: this.config.maxBodyLength,
            captureBody: this.config.captureBody,
          }),
        })

        // Reconstruct response with the body we already read
        return new Response(bodyForCaller, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        })
      } catch (error) {
        const duration = Date.now() - startTime

        this.addEvent({
          type: 'network',
          subtype: 'fetch',
          method: method.toUpperCase(),
          url: typeof url === 'string' ? url : url.url,
          duration,
          error: error.message,
          requestHeaders: redactHeaders(normalizedRequestHeaders, this.config.sensitiveHeaders),
          requestBody: redactBody(
            options.body,
            normalizedRequestHeaders?.['Content-Type'] ?? normalizedRequestHeaders?.['content-type'],
            {
              sensitiveFields: this.config.sensitiveFields,
              maxLength: this.config.maxBodyLength,
              captureBody: this.config.captureBody,
            }
          ),
        })

        throw error
      }
    }
  }

  /**
   * Uninstall fetch interceptor
   */
  uninstallFetchInterceptor() {
    if (this.originalFetch) {
      window.fetch = this.originalFetch
      this.originalFetch = null
    }
  }

  /**
   * Install XHR interceptor
   */
  installXhrInterceptor() {
    const xhrData = new WeakMap()

    this.originalXhrOpen = XMLHttpRequest.prototype.open
    this.originalXhrSend = XMLHttpRequest.prototype.send

    const { config } = this
    const addEvent = this.addEvent.bind(this)
    const parseResponseHeaders = this.parseResponseHeaders.bind(this)
    const { originalXhrOpen, originalXhrSend } = this

    XMLHttpRequest.prototype.open = function xhrOpenInterceptor(method, url, ...rest) {
      xhrData.set(this, {
        method,
        url,
        startTime: null,
        requestHeaders: {},
      })
      return originalXhrOpen.call(this, method, url, ...rest)
    }

    XMLHttpRequest.prototype.send = function xhrSendInterceptor(body) {
      const data = xhrData.get(this)
      if (data) {
        data.startTime = Date.now()
        data.requestBody = body
      }

      const onLoadEnd = () => {
        if (!data) return

        const duration = Date.now() - data.startTime

        addEvent({
          type: 'network',
          subtype: 'xhr',
          method: data.method,
          url: data.url,
          status: this.status,
          statusText: this.statusText,
          duration,
          requestHeaders: redactHeaders(data.requestHeaders, config.sensitiveHeaders),
          requestBody: redactBody(data.requestBody, data.requestHeaders['Content-Type'], {
            sensitiveFields: config.sensitiveFields,
            maxLength: config.maxBodyLength,
            captureBody: config.captureBody,
          }),
          responseHeaders: redactHeaders(parseResponseHeaders(this.getAllResponseHeaders()), config.sensitiveHeaders),
          responseBody: redactBody(this.responseText, this.getResponseHeader('content-type'), {
            sensitiveFields: config.sensitiveFields,
            maxLength: config.maxBodyLength,
            captureBody: config.captureBody,
          }),
        })

        // CRITICAL: Remove listener after firing to prevent memory leak
        this.removeEventListener('loadend', onLoadEnd)

        // Clear WeakMap entry to allow GC
        xhrData.delete(this)
      }

      this.addEventListener('loadend', onLoadEnd)
      return originalXhrSend.call(this, body)
    }

    // Intercept setRequestHeader — store on instance so uninstall can restore it
    this.originalXhrSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader
    const { originalXhrSetRequestHeader } = this
    XMLHttpRequest.prototype.setRequestHeader = function xhrSetRequestHeaderInterceptor(header, value) {
      const data = xhrData.get(this)
      if (data) {
        data.requestHeaders[header] = value
      }
      return originalXhrSetRequestHeader.call(this, header, value)
    }
  }

  /**
   * Parse XHR response headers string into object
   */
  parseResponseHeaders(headersString) {
    const headers = {}
    if (!headersString) return headers

    headersString.split('\r\n').forEach((line) => {
      const parts = line.split(': ')
      if (parts.length === 2) {
        headers[parts[0]] = parts[1]
      }
    })

    return headers
  }

  /**
   * Uninstall XHR interceptor
   */
  uninstallXhrInterceptor() {
    if (this.originalXhrOpen) {
      XMLHttpRequest.prototype.open = this.originalXhrOpen
      this.originalXhrOpen = null
    }
    if (this.originalXhrSend) {
      XMLHttpRequest.prototype.send = this.originalXhrSend
      this.originalXhrSend = null
    }
    if (this.originalXhrSetRequestHeader) {
      XMLHttpRequest.prototype.setRequestHeader = this.originalXhrSetRequestHeader
      this.originalXhrSetRequestHeader = null
    }
  }

  /**
   * Install navigation interceptors (history API and native events)
   */
  installNavigationInterceptors() {
    // Intercept pushState (does not emit native events)
    this.originalPushState = history.pushState
    history.pushState = (...args) => {
      const [state, , url] = args
      this.addEvent({
        type: 'navigation',
        subtype: 'pushState',
        url: url || window.location.href,
        state,
      })
      return this.originalPushState.apply(history, args)
    }

    // Intercept replaceState (does not emit native events)
    this.originalReplaceState = history.replaceState
    history.replaceState = (...args) => {
      const [state, , url] = args
      this.addEvent({
        type: 'navigation',
        subtype: 'replaceState',
        url: url || window.location.href,
        state,
      })
      return this.originalReplaceState.apply(history, args)
    }

    // Listen to popstate (back/forward)
    this.popStateListener = () => {
      this.addEvent({
        type: 'navigation',
        subtype: 'popstate',
        url: window.location.href,
      })
    }
    window.addEventListener('popstate', this.popStateListener)

    // Listen to hashchange
    this.hashChangeListener = (event) => {
      this.addEvent({
        type: 'navigation',
        subtype: 'hashchange',
        oldURL: event.oldURL,
        newURL: event.newURL,
      })
    }
    window.addEventListener('hashchange', this.hashChangeListener)
  }

  /**
   * Uninstall navigation interceptors
   */
  uninstallNavigationInterceptors() {
    if (this.originalPushState) {
      history.pushState = this.originalPushState
      this.originalPushState = null
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState
      this.originalReplaceState = null
    }
    if (this.popStateListener) {
      window.removeEventListener('popstate', this.popStateListener)
      this.popStateListener = null
    }
    if (this.hashChangeListener) {
      window.removeEventListener('hashchange', this.hashChangeListener)
      this.hashChangeListener = null
    }
  }

  /**
   * Install console interceptors
   */
  installConsoleInterceptors() {
    const createConsoleInterceptor = (level, original) => {
      return (...args) => {
        this.addEvent({
          type: 'console',
          level,
          message: args
            .map((arg) => {
              try {
                return typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
              } catch {
                return '[Unable to stringify]'
              }
            })
            .join(' '),
        })
        return original.apply(console, args)
      }
    }

    this.originalConsoleLog = console.log
    this.originalConsoleWarn = console.warn
    this.originalConsoleError = console.error
    this.originalConsoleInfo = console.info
    this.originalConsoleDebug = console.debug

    console.log = createConsoleInterceptor('log', this.originalConsoleLog)
    console.warn = createConsoleInterceptor('warn', this.originalConsoleWarn)
    console.error = createConsoleInterceptor('error', this.originalConsoleError)
    console.info = createConsoleInterceptor('info', this.originalConsoleInfo)
    console.debug = createConsoleInterceptor('debug', this.originalConsoleDebug)
  }

  /**
   * Uninstall console interceptors
   */
  uninstallConsoleInterceptors() {
    if (this.originalConsoleLog) {
      console.log = this.originalConsoleLog
      this.originalConsoleLog = null
    }
    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn
      this.originalConsoleWarn = null
    }
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError
      this.originalConsoleError = null
    }
    if (this.originalConsoleInfo) {
      console.info = this.originalConsoleInfo
      this.originalConsoleInfo = null
    }
    if (this.originalConsoleDebug) {
      console.debug = this.originalConsoleDebug
      this.originalConsoleDebug = null
    }
  }

  /**
   * Install error handlers
   */
  installErrorHandlers() {
    // Uncaught exceptions
    this.errorListener = (event) => {
      this.addEvent({
        type: 'error',
        subtype: 'uncaught',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
          ? {
              name: event.error.name,
              message: event.error.message,
              stack: event.error.stack,
            }
          : undefined,
      })
    }
    window.addEventListener('error', this.errorListener)

    // Unhandled promise rejections
    this.unhandledRejectionListener = (event) => {
      this.addEvent({
        type: 'error',
        subtype: 'unhandledRejection',
        reason:
          event.reason instanceof Error
            ? {
                name: event.reason.name,
                message: event.reason.message,
                stack: event.reason.stack,
              }
            : String(event.reason),
      })
    }
    window.addEventListener('unhandledrejection', this.unhandledRejectionListener)
  }

  /**
   * Uninstall error handlers
   */
  uninstallErrorHandlers() {
    if (this.errorListener) {
      window.removeEventListener('error', this.errorListener)
      this.errorListener = null
    }
    if (this.unhandledRejectionListener) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionListener)
      this.unhandledRejectionListener = null
    }
  }
}

// Shared singleton instance
let sharedCollector = null

/**
 * Get or create the shared collector instance
 */
export function getSharedCollector(config) {
  if (!sharedCollector) {
    sharedCollector = new EventCollector(config)
  }
  return sharedCollector
}

/**
 * Reset the shared collector (useful for testing)
 */
export function resetSharedCollector() {
  if (sharedCollector) {
    sharedCollector.uninstall()
    sharedCollector = null
  }
}
